import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { addTags, deleteTags } from 'api/contacts/single';
import * as Dialog from 'components/dialog';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Form from 'components/form';
import { ListItem } from 'components/list-item';
import * as Select from 'components/select';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentAccountTags } from 'customHooks/useCurrentAccountTags';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface UpdateTagsModalLocationState {
    updateType: 'add' | 'remove';
    contacts: Contact[];
}

interface UpdateTagsModalFormValues {
    tags: string[];
}

const UpdateTagsModal = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { data: accountTags = [] } = useCurrentAccountTags();
    const newTagRef = useRef<HTMLLIElement>(null);
    const [tempTag, setTempTag] = useState('');
    const location = useLocation<UpdateTagsModalLocationState | undefined>();
    const { updateType, contacts = [] } = location.state || {};
    const isAdd = updateType === 'add';

    useEffect(() => {
        if (!updateType || contacts.length === 0) {
            goBack();
        }
    }, [contacts, updateType, goBack]);

    const methods = Form.useGivebutterForm<UpdateTagsModalFormValues>({
        defaultValues: {
            tags: [],
        },
    });
    const { control, handleSubmit, watch, setError } = methods;
    const tags = watch('tags');

    const contactsIds = useMemo(() => contacts.map(item => item.id), [contacts]);
    const options = useMemo(() => {
        const options = isAdd
            ? accountTags.map(item => item.name)
            : Array.from(
                  new Set(contacts.reduce((acc, item) => [...acc, ...item.tags], [] as string[])),
              );
        return Array.from(new Set([...options, ...tags]));
    }, [isAdd, accountTags, contacts, tags]);

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: () => {
            const handler = isAdd ? addTags : deleteTags;
            return handler({ tags, accountId, contactsIds });
        },
        onSuccess: () => {
            toast.success('Tags updated successfully');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;

            if (errors) {
                for (let i in errors) {
                    setError('tags', {
                        message: errors[i][0],
                    });
                }
            } else {
                toast.error(isAdd ? 'Unable to add tags' : 'Unable to remove tags');
            }
        },
    });

    return (
        <Dialog.Body className="flex flex-col gap-4">
            <Dialog.Close />
            <Dialog.Title>{isAdd ? 'Add Tags' : 'Remove Tags'}</Dialog.Title>
            <Dialog.Description className="font-light">
                You are {isAdd ? 'adding' : 'removing'} these tags {isAdd ? 'to' : 'from'}{' '}
                <span className="px-0.5 font-medium">{contacts.length} selected</span>{' '}
                {pluralize(contacts.length, 'contact')}.
            </Dialog.Description>

            <Form.Provider {...methods}>
                <form onSubmit={handleSubmit(() => submit())} className={'flex flex-col gap-8'}>
                    <Form.Container>
                        <Form.Label name={'tags'}>Tags</Form.Label>
                        <Select.UseFormSearchableSelect
                            name={'tags'}
                            control={control}
                            size="lg"
                            multi={true}
                            inputProps={{
                                value: tempTag,
                                placeholder: 'Tags',
                                onChange: e => setTempTag(e.currentTarget.value),
                                onKeyDown: e => {
                                    const value = e.currentTarget.value;
                                    if (e.key === 'Enter' && value.length > 0) {
                                        e.preventDefault();
                                        newTagRef.current?.click();
                                    }
                                },
                            }}
                            onSelectItem={() => {
                                setTempTag('');
                            }}
                        >
                            {isAdd && tempTag.length > 0 && !tags.find(tag => tag === tempTag) && (
                                <Select.SelectListItem value={tempTag} ref={newTagRef}>
                                    <p className="font-light">
                                        Add <span className="font-medium">{tempTag}</span> (press
                                        enter)
                                    </p>
                                </Select.SelectListItem>
                            )}

                            {options.map(item => (
                                <Select.SelectListItem key={item} value={item} checkable={true}>
                                    {item}
                                </Select.SelectListItem>
                            ))}

                            {!isAdd && options.length === 0 && (
                                <ListItem className="!cursor-default text-center">
                                    No Tags were found for the selected Contacts.
                                </ListItem>
                            )}
                        </Select.UseFormSearchableSelect>
                    </Form.Container>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" color="neutral" onClick={() => goBack()}>
                            Cancel
                        </Button>
                        <Form.SubmitButton isSubmitting={isLoading} disabled={tags.length === 0}>
                            {isAdd ? 'Add' : 'Remove'} {tags.length} tag
                            {tags.length !== 1 && 's'}
                        </Form.SubmitButton>
                    </div>
                </form>
            </Form.Provider>
        </Dialog.Body>
    );
};

export { UpdateTagsModal };
export type { UpdateTagsModalLocationState };
